import {
  Icon,
  Button,
  Divider,
  Modal,
  Loader,
  Tag,
} from "@src/components/atoms";
import { CardContainer } from "@src/components/cards";
import { TeamForm } from "@src/components/forms/admin/TeamForm";
import { TargetForm } from "@src/components/forms/admin/TargetForm";
import { useState } from "react";
import AdminHooks from "@src/context/hooks/adminHooks";
import { useAuthContext } from "@src/context";
import moment from "moment";
import { getBehaviourColourForPosition } from "@signolteam/shared/colours";

export const TeamsSection = ({ customerId, customerCode, industry }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingTargets, setIsEditingTargets] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamTargets, setTeamTargets] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [selectedBehaviour, setSelectedBehaviour] = useState(null);
  const [hasChanged, setHasChanged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const auth = useAuthContext();

  const updateMutation = AdminHooks.useUpdateTeam();
  const createMutation = AdminHooks.useCreateTeam();
  const deleteMutation = AdminHooks.useDeleteTeam();

  const updateTargetMutation = AdminHooks.useUpdateTarget();
  const createTargetMutation = AdminHooks.useCreateTarget();
  const deleteTargetMutation = AdminHooks.useDeleteTarget();

  const {
    data: teams,
    isLoading,
    isPending,
    isSettled,
    isError,
    //error: errorAll,
  } = AdminHooks.useGetTeams(auth.activeRole, customerId);

  const {
    data: behaviours,
    isLoadingBehaviours,
    isPendingBehaviours,
    isSettledBehaviours,
    isErrorBehaviours,
    //error: errorAll,
  } = AdminHooks.useGetBehaviours(auth.activeRole, customerCode);

  const handleSubmitChanges = async (data) => {
    setSubmitting(true);
    setError(null);

    try {
      //TODO - Format teams data for body
      // await updateMutation.mutateAsync(data);
      console.log("teams data", data);
    } catch (err) {
      setError("Failed to submit teams update");
    } finally {
      setSubmitting(false);
      setHasChanged(false);
      setSubmitted(true);
    }
  };
  return (
    <div className="p-4">
      {!hasChanged && !!submitted && (
        <div className="grow text-green-500 border border-1-green rounded-2xl p-2 bg-green-100">
          <p>Your changes have been submitted successfully.</p>
        </div>
      )}
      {isLoading && <Loader size="2xl" />}
      {!isLoading && isEditingTargets && (
        <div className="border-1 border-blue-300 rounded-lg p-2 bg-stone-100 mb-4">
          <h5>Team {selectedTeam?.name} Targets</h5>
          {teamTargets?.map((target, i) => {
            const bvr = behaviours.find(
              (behaviour) =>
                behaviour.behaviourTypeId === target.behaviourTypeId,
            );
            const bvrColor = getBehaviourColourForPosition(bvr?.position).name;
            return (
              <div
                key={target.id}
                className={`p-3 bg-${bvrColor}-${i % 2 == 0 ? 200 : 100}`}
              >
                <h6>
                  ({bvr?.acronym}) {target.name}
                  {target.targetPercentage
                    ? ` - ${target.targetPercentage}%`
                    : ""}
                  {target.startDate
                    ? ` - ${moment.utc(target.startDate).format("MMM D, YYYY")}`
                    : ""}
                </h6>
                <Button
                  icon={"feedback"}
                  padding={false}
                  block={false}
                  secondary
                  onClick={() => setSelectedTarget(target)}
                >
                  Edit
                </Button>{" "}
                <Button
                  icon={"close"}
                  color="red"
                  padding={false}
                  block={false}
                  secondary
                  onClick={async () => {
                    await deleteTargetMutation.mutateAsync({
                      customerId,
                      teamId: selectedTeam.id,
                      targetId: target.id,
                    });
                    setTeamTargets(
                      teamTargets.filter((t) => t.id !== target.id),
                    );
                    setHasChanged(true);
                  }}
                >
                  Delete
                </Button>
              </div>
            );
          })}
          <Button
            icon={"feedback"}
            block={false}
            secondary
            onClick={() => setSelectedTarget({})}
          >
            Add Target
          </Button>
          <Button
            icon={"close"}
            color="red"
            block={false}
            secondary
            onClick={() => {
              setTeamTargets([]);
              setSelectedTarget(null);
              setIsEditingTargets(false);
              setSelectedTeam(null);
            }}
          >
            Close
          </Button>
          {teamTargets?.length === 0 && "No targets yet"}
        </div>
      )}
      {!isLoading && isEditingTargets && selectedTarget && (
        <TargetForm
          key={selectedTarget?.name || "new-target"}
          data={selectedTarget}
          behaviours={behaviours}
          isNew={!selectedTarget?.id}
          onSubmit={async (data) => {
            data.id = selectedTarget?.id;
            if (!selectedTarget?.id) {
              await createTargetMutation.mutateAsync({
                customerId,
                teamId: selectedTeam.id,
                data,
              });
              setTeamTargets((prev) => [...prev, data]);
            } else {
              await updateTargetMutation.mutateAsync({
                customerId,
                teamId: selectedTeam.id,
                data,
              });
            }
            setHasChanged(true);
            !!submitted && setSubmitted(false);
            setSelectedTarget(null);
          }}
        />
      )}
      {!isLoading && !teams.length && (
        <div>
          <div>No teams set for this customer</div>
          <div className="w-64">
            <Button icon={"plus"} block={false} secondary onClick={setIsAdding}>
              Add team
            </Button>
          </div>
        </div>
      )}
      {!isLoading && teams.length && !isEditingTargets && (
        <>
          {teams
            ?.sort((a, b) => b.isDefault - a.isDefault)
            .map((team, index) => (
              <div key={`${team.name}-${index}`} className="py-4">
                {selectedTeam?.name !== team?.name ? (
                  <CardContainer key={team?.name} padding={4}>
                    <h4 className="m-2">
                      {team.name}
                      {team.alias && ` (${team.alias})`}
                    </h4>
                    <div className="m-2">
                      {team.isDefault && (
                        <Tag color="primary">
                          {"Default Team "}
                          <Icon name="check" textColor="blue-400" inline />
                        </Tag>
                      )}
                    </div>
                    <div className="m-2 flex-col">
                      <h5>{"Baseline Success Rate Start Date: "}</h5>
                      {team.baselineSuccessRateStartDate
                        ? moment
                            .utc(team.baselineSuccessRateStartDate)
                            .format("MM-DD-YY")
                        : "Default"}
                      <h5>{"Baseline Success Rate End Date: "}</h5>
                      {team.baselineSuccessRateEndDate
                        ? moment
                            .utc(team.baselineSuccessRateEndDate)
                            .format("MM-DD-YY")
                        : "Default"}
                      <h5>{"Baseline Event Count End Date: "}</h5>
                      {team.baselineEventCountEndDate
                        ? moment
                            .utc(team.baselineEventCountEndDate)
                            .format("MM-DD-YY")
                        : "Default"}
                    </div>
                    <div className="border-1 border-blue-300 rounded-lg p-2 bg-stone-100 m-2">
                      <h5>Targets</h5>
                      {team.targets?.map((target, i) => {
                        const bvr = behaviours?.find(
                          (behaviour) =>
                            behaviour.behaviourTypeId ===
                            target.behaviourTypeId,
                        );
                        const bvrColor = getBehaviourColourForPosition(
                          bvr?.position,
                        ).name;
                        return (
                          <h6
                            key={target.id}
                            className={`p-2 bg-${bvrColor}-200`}
                          >
                            ({bvr?.acronym}) {target.name}
                            {target.targetPercentage
                              ? ` - ${target.targetPercentage}%`
                              : ""}
                            {target.startDate
                              ? ` - ${moment.utc(target.startDate).format("MMM D, YYYY")}`
                              : ""}
                          </h6>
                        );
                      })}
                      {team.targets?.length === 0 && "No targets yet"}
                    </div>
                    <div className="flex w-48">
                      <Button
                        icon={"feedback"}
                        block={false}
                        secondary
                        onClick={() => setSelectedTeam(team)}
                      >
                        Edit
                      </Button>
                      <div>
                        <Button
                          icon={"feedback"}
                          color="yellow"
                          block={false}
                          secondary
                          onClick={() => {
                            setSelectedTeam(team);
                            setTeamTargets(team.targets);
                            setIsEditingTargets(true);
                          }}
                        >
                          Targets
                        </Button>
                      </div>
                      <div>
                        <Button
                          icon={"close"}
                          color="red"
                          block={false}
                          secondary
                          onClick={() => {
                            // ask for confirmation before deleting
                            if (
                              window.confirm(
                                `Are you sure you want to delete team ${team.name}? This action cannot be undone.`,
                              )
                            ) {
                              // ask for double confirmation
                              if (
                                window.confirm(
                                  `Are you absolutely sure you want to delete team ${team.name}? This action cannot be undone.`,
                                )
                              ) {
                                deleteMutation.mutateAsync({
                                  customerId,
                                  teamId: team.id,
                                });
                              }
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </CardContainer>
                ) : (
                  <CardContainer padding={5} thickBorderColor="primary">
                    <TeamForm
                      key={team.name}
                      data={selectedTeam}
                      onSubmit={async (data) => {
                        console.log("existing team amended", {
                          ...data,
                          id: team.id,
                        });
                        await updateMutation.mutateAsync({
                          customerId,
                          team: { ...data, id: team.id },
                        });
                        setHasChanged(true);
                        !!submitted && setSubmitted(false);
                        setSelectedTeam(null);
                      }}
                      industry={industry}
                    />
                  </CardContainer>
                )}
              </div>
            ))}
          <div className="w-64">
            <Button icon={"plus"} block={false} secondary onClick={setIsAdding}>
              Add team
            </Button>
          </div>
        </>
      )}

      {isAdding && (
        <Modal
          open={isAdding}
          handleClose={() => setIsAdding(false)}
          children={
            <div className="py-4 text-center">
              <h4>New team details</h4>
              <Divider />
              <TeamForm
                key={"new-team"}
                isNew={isAdding}
                onSubmit={async (data) => {
                  console.log("new team created", { customerId, data });
                  await createMutation.mutateAsync({ customerId, team: data });
                  setHasChanged(true);
                  !!submitted && setSubmitted(false);
                  setSelectedTeam(null);
                  setIsAdding(false);
                }}
                industry={industry}
              />
            </div>
          }
        />
      )}
      <Divider />
    </div>
  );
};
